import { bind, classNames, Component, h, signal, tag } from "omi";

import { storageSignal } from "@/utils/signals/storageSignal";
import { IconName, NWIcon } from "@/components/atoms";
import { tailwind } from "@/tailwind";

export * from "./nw-accordion-item";

type UncontrolledProps = {
  type?: "uncontrolled";
  openByDefault?: boolean;
};

type ControlledProps = {
  type?: "controlled";
  onChange: (isOpen: boolean) => void;
  isOpen: boolean;
};

type AccordionProps = {
  storageKey?: string;
  icon?: IconName;
  label?: string;
  fullWidth?: boolean;
  onLabelClick?: () => void;
  textSize?: "sm" | "md";
  textColor?: "dark-gray" | "light-grey";
  nested?: boolean;
  useSlotForLabel?: boolean;
};

type Props = AccordionProps & (UncontrolledProps | ControlledProps);

@tag("nw-accordion")
export class NWAccordion extends Component<Props> {
  static css = [tailwind];
  static propTypes = {
    icon: String,
    label: String,
    openByDefault: Boolean,
    onLabelClick: Boolean,
    textSize: String,
    textColor: String,
    type: String,
    onChange: Function,
    isOpen: Boolean,
    storageKey: String,
    nested: Boolean,
    useSlotForLabel: Boolean,
    fullWidth: Boolean,
  };

  static defaultProps = {
    type: "uncontrolled",
  };

  private isOpenState = signal<boolean>(false);

  @bind
  install() {
    if (this.props.storageKey != null) {
      this.isOpenState = storageSignal<boolean>(this.props.storageKey, { default: false });
    }

    if (this.props.type === "controlled") {
      this.isOpenState.value = this.props.isOpen;
    } else if (this.props.type === "uncontrolled" && this.props.openByDefault) {
      this.isOpenState.value = this.props.openByDefault;
    }
  }

  // receiveProps(props: OmiProps<Props> | Props, oldProps: OmiProps<Props> | Props) {
  //   if (props.openByDefault !== oldProps.openByDefault) {
  //     this.isOpenState.value = true;
  //   } else {
  //     return false;
  //   }
  // }

  @bind
  private handleLabelClick(e: MouseEvent) {
    e.preventDefault();
    this.props.onLabelClick && this.props.onLabelClick();
  }

  @bind
  private hasSlottedChildren(): boolean {
    return !!this.children.length;
  }

  @bind
  private getStateHandlers(type: "uncontrolled" | "controlled"): [boolean, () => void] {
    const isOpen = this.props.type === "controlled" ? (this.props as ControlledProps).isOpen : this.isOpenState!.value;

    const changeHandler =
      type === "controlled"
        ? () => (this.props as ControlledProps).onChange(!isOpen)
        : () => (this.isOpenState!.value = !this.isOpenState!.value);
    return [isOpen, changeHandler];
  }

  render({
    nested,
    icon,
    label,
    onLabelClick,
    fullWidth = false,
    useSlotForLabel = false,
    textSize = "md",
    textColor = "dark-gray",
    type = "uncontrolled",
  }: Props) {
    const [isOpen, toggleIsOpen] = this.getStateHandlers(type);

    const hasChildren = this.hasSlottedChildren();
    const splitTriggers = !!onLabelClick;

    return (
      <div class={classNames({ "pl-4": nested, "w-full": fullWidth, "max-w-sm": !fullWidth })} tabIndex={-1}>
        <button
          tabIndex={0}
          type="button"
          class={classNames(
            "flex items-center justify-between w-full hover:bg-secondary rounded-md transition-all select-none",
            { "text-primary": textColor === "dark-gray", "text-quaternary": textColor === "light-grey" },
          )}
          onClick={!hasChildren ? this.handleLabelClick : splitTriggers ? this.handleLabelClick : toggleIsOpen}
        >
          {icon ? (
            <div class="inline-flex items-center space-x-3 w-full pl-3 py-2">
              <div class="shrink-0">
                <NWIcon name={icon} width={16} height={16} />
              </div>
              <h4 class={classNames("truncate", { "text-sm": textSize === "sm" })}>{label}</h4>
            </div>
          ) : (
            <div
              class={classNames("pl-3 py-2 truncate w-full text-left space-x-4", {
                "text-sm": textSize === "sm",
              })}
            >
              {useSlotForLabel ? <slot name="label"></slot> : label}
            </div>
          )}

          {hasChildren ? (
            <div
              class={classNames("self-stretch flex justify-center items-center py-2 w-10")}
              onClick={
                splitTriggers
                  ? e => {
                      e.stopImmediatePropagation();
                      toggleIsOpen();
                    }
                  : undefined
              }
            >
              <NWIcon
                name="chevron-down"
                width={12}
                height={12}
                className={classNames("size-3 transition-all shrink-0 text-quaternary", {
                  "rotate-180": isOpen,
                  "rotate-0": !isOpen,
                })}
              />
            </div>
          ) : (
            <div class="w-10 self-stretch"></div>
          )}
        </button>

        <div
          class={classNames(
            "transition-all mt-1",
            {
              "max-h-0 opacity-0 duration-400 pointer-events-none": !isOpen,
              "max-h-screen opacity-100 duration-500": isOpen,
            },
            { "pl-4": nested },
          )}
        >
          <slot></slot>
        </div>
      </div>
    );
  }
}
